import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Brain,
  Target,
  Rocket,
  CheckCircle,
  Sparkles,
  TrendingUp,
  Shield,
  Zap,
} from "lucide-react";
import "../styles/Home.css";

const Home = () => {
  const services = [
    {
      icon: <Brain />,
      title: "AI Consulting",
      description: "We help organisations from idea to implementation",
      features: [
        "Efficiency through intelligent automation",
        "Structured decision support systems",
        "Scalable AI workflows, prototypes, and products",
      ],
    },
    {
      icon: <Target />,
      title: "GTM Strategy & Marketing",
      description: "A complete, modern GTM engine designed for sustained momentum",
      features: [
        "Market identification",
        "Narrative and value clarity",
        "Launch planning and growth systems",
        "Performance with predictable results",
      ],
    },
    {
      icon: <Rocket />,
      title: "Product Acceleration",
      description: "We guide products from concept to launch with discipline and speed",
      features: [
        "Product Roadmap and Architecture",
        "MVP development",
        "Market validation",
        "Iterative scaling frameworks",
      ],
    },
  ];

  const whyPartner = [
    {
      icon: <Sparkles />,
      title: "End-to-End Expertise",
      description: "Strategic, technical, and operational expertise",
    },
    {
      icon: <Zap />,
      title: "Clear Communication",
      description: "Direct and outcome-oriented approach",
    },
    {
      icon: <TrendingUp />,
      title: "Faster Decisions",
      description: "Proven frameworks that accelerate progress",
    },
    {
      icon: <Shield />,
      title: "Structured Approach",
      description: "Collaborative and accountable engagements",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  // Deterministic positions (avoid Math.random() on each render)
  const flowLines = Array.from({ length: 5 }).map((_, i) => ({
    top: 20 + i * 15,
    delay: i * 0.5,
    opacity: 0.3 - i * 0.05,
  }));

  const neurons = [
    { top: 18, left: 12, secondary: false, delay: 0.2 },
    { top: 28, left: 32, secondary: true, delay: 1.1 },
    { top: 22, left: 62, secondary: false, delay: 0.7 },
    { top: 38, left: 82, secondary: true, delay: 1.6 },
    { top: 55, left: 70, secondary: false, delay: 0.4 },
    { top: 66, left: 46, secondary: true, delay: 2.0 },
    { top: 72, left: 18, secondary: false, delay: 1.4 },
    { top: 48, left: 16, secondary: true, delay: 0.9 },
  ];

  const synapses = [
    { w: 220, top: 24, left: 18, rot: 18, delay: 0.2 },
    { w: 260, top: 32, left: 44, rot: -22, delay: 1.0 },
    { w: 200, top: 44, left: 24, rot: 38, delay: 1.6 },
    { w: 240, top: 58, left: 40, rot: -12, delay: 0.7 },
    { w: 210, top: 66, left: 60, rot: 28, delay: 1.3 },
    { w: 180, top: 72, left: 28, rot: -34, delay: 2.1 },
  ];

  const shapes = [
    { top: 22, left: 76, delay: 0, dur: 20 },
    { top: 50, left: 86, delay: 3, dur: 25 },
    { top: 74, left: 66, delay: 6, dur: 30 },
  ];

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        {/* Existing background layers (keep) */}
        <div className="gradient-mesh" />
        <div className="ai-grid" />

        {/* Cosmos + AI layer merged across the full hero */}
        <div className="cosmos-scene cosmos-scene--bg" aria-hidden="true">
          <div className="cosmos-glow" />

          <div className="orbit orbit-1">
            <span className="planet planet-a" />
          </div>
          <div className="orbit orbit-2">
            <span className="planet planet-b" />
          </div>
          <div className="orbit orbit-3">
            <span className="planet planet-c" />
          </div>

          <div className="ai-core">
            <div className="ai-core-inner" />
            <div className="ai-core-ring" />
            <div className="ai-core-ring ring-2" />
          </div>

          <svg className="constellation" viewBox="0 0 600 420" preserveAspectRatio="none">
            <path d="M80 280 L170 210 L260 250 L330 140 L430 190 L520 120" />
            <path d="M120 320 L210 300 L290 340 L370 260 L470 300" />
          </svg>

          <div className="spark s1" />
          <div className="spark s2" />
          <div className="spark s3" />
          <div className="spark s4" />
        </div>

        {/* Data Flow Lines (keep) */}
        <div className="data-flow" aria-hidden="true">
          {flowLines.map((l, i) => (
            <div
              key={i}
              className="flow-line"
              style={{
                top: `${l.top}%`,
                animationDelay: `${l.delay}s`,
                opacity: l.opacity,
              }}
            />
          ))}
        </div>

        {/* Neural Network Visualization (keep) */}
        <div className="neural-network" aria-hidden="true">
          {neurons.map((n, i) => (
            <div
              key={`neuron-${i}`}
              className={`neuron ${n.secondary ? "secondary" : ""}`}
              style={{
                top: `${n.top}%`,
                left: `${n.left}%`,
                animationDelay: `${n.delay}s`,
              }}
            />
          ))}

          {synapses.map((s, i) => (
            <div
              key={`synapse-${i}`}
              className="synapse"
              style={{
                width: `${s.w}px`,
                top: `${s.top}%`,
                left: `${s.left}%`,
                transform: `rotate(${s.rot}deg)`,
                animationDelay: `${s.delay}s`,
              }}
            />
          ))}
        </div>

        {/* Floating 3D Shapes (keep) */}
        <div className="floating-shapes" aria-hidden="true">
          {shapes.map((sh, i) => (
            <div
              key={`shape-${i}`}
              className="shape-3d"
              style={{
                top: `${sh.top}%`,
                left: `${sh.left}%`,
                animationDelay: `${sh.delay}s`,
                animationDuration: `${sh.dur}s`,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="container-wide">
          <div className="hero-layout">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="hero-content hero-left"
            >
              <motion.h1
                className="hero-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                Scale starts here.
                {/* <span className="text-primary"> Launch Faster.</span> */}
              </motion.h1>

              <motion.p
                className="hero-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                Kosmoz Consulting is an AI and Go-to-Market consulting partner for businesses
                that require precision, speed, and strategic clarity.
              </motion.p>

              {/* Optional CTA row (keep if you want buttons here) */}
              {/* <div className="hero-actions">
                <Link to="/services" className="btn btn-primary">Explore Services</Link>
                <Link to="/about" className="btn">About Us</Link>
              </div> */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="overview" className="section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            className="section-header"
          >
            <h2>What We Do — At a Glance</h2>
            <p className="section-subtitle">
              Comprehensive solutions across AI, GTM, and Product Development
            </p>
          </motion.div>

          <motion.div
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {services.map((service) => (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className="service-card k-card"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={18} className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/services" className="service-link">
                  Learn More <ArrowRight size={18} />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Partner */}
      <section id="why-partner" className="section bg-light">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            className="section-header"
          >
            <h2>Why Organizations Partner With Us</h2>
              <p className="section-subtitle">
                Enterprise leaders work with Kosmoz because we deliver measurable progress, grounded in deep industry expertise 
              </p>
          </motion.div>

          <motion.div
            className="why-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {whyPartner.map((item) => (
              <motion.div
                key={item.title}
                variants={itemVariants}
                className="why-card"
                whileHover={{
                  scale: 1.05,
                  rotate: [0, 1, -1, 0],
                  transition: { duration: 0.3 },
                }}
              >
                <div className="why-icon">{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Orbit Model */}
      <section id="orbit-model" className="section">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            className="section-header"
          >
            <h2>Approach — The Orbit Model</h2>
            <p className="section-subtitle">
              A streamlined approach built for alignment, execution, and scale
            </p>
          </motion.div>

          <div className="orbit-model">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="orbit-step"
              whileHover={{ scale: 1.05 }}
            >
              <div className="orbit-number">1</div>
              <div className="orbit-content">
                <h3>Discover</h3>
                <p>Clarifies goals, constraints, risks, and opportunities</p>
              </div>
            </motion.div>

            <motion.div
              className="orbit-arrow"
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              →
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="orbit-step"
              whileHover={{ scale: 1.05 }}
            >
              <div className="orbit-number">2</div>
              <div className="orbit-content">
                <h3>Design</h3>
                <p>Creates the roadmap, systems, workflows, and messaging</p>
              </div>
            </motion.div>

            <motion.div
              className="orbit-arrow"
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              →
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="orbit-step"
              whileHover={{ scale: 1.05 }}
            >
              <div className="orbit-number">3</div>
              <div className="orbit-content">
                <h3>Deploy</h3>
                <p>Ensures delivery with momentum and measurable outcomes</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="orbit-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link to="/about" className="btn btn-primary">
              Build With Confidence <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;